import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl, config, requestID } from "../../../common/utils";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Stack,
  CircularProgress, Paper, Container, Chip, Avatar,
  Grid, Badge, LinearProgress, Alert as MuiAlert, AlertTitle,
  Button, Divider, Tooltip
} from '@mui/material';
import {
  Warning as WarningIcon,
  Dangerous as DangerousIcon,
  HourglassBottom as ExpiredIcon,
  DeleteSweep as WriteOffIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
  TrendingDown as TrendingDownIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { IonContent, IonPage } from '@ionic/react';

interface WriteOffLog {
  id: number;
  stock_name: string;
  ecid_item: string;
  quantity: number;
  date_time: string;
  from_location: string;
  reason: string;
  signer_out_name: string;
}

interface StockAlertStats {
  totalWriteOffs: number;
  recentWriteOffs: number;
  topReason: string;
  totalLostValue: number;
}

const StocksAlert: React.FC = () => {
  const [logs, setLogs] = useState<WriteOffLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StockAlertStats>({
    totalWriteOffs: 0,
    recentWriteOffs: 0,
    topReason: 'Damaged',
    totalLostValue: 0
  });
  const [filter, setFilter] = useState<string>('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const res = await axios.post(`${baseUrl()}Geacloud_Inventorys/getWriteOffLogs`, formData, config);
      if (res.data.status) {
        const data = res.data.data || [];
        setLogs(data);
        calculateStats(data);
      }
    } catch (err) {
      console.error('Error fetching write-off logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: WriteOffLog[]) => {
    const totalWriteOffs = data.length;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentWriteOffs = data.filter(log =>
      new Date(log.date_time) >= thirtyDaysAgo
    ).length;

    // Count reasons
    const reasonCounts: Record<string, number> = {};
    data.forEach(log => {
      reasonCounts[log.reason] = (reasonCounts[log.reason] || 0) + 1;
    });
    const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Damaged';

    // Calculate approximate lost value (you might need actual price data)
    const totalLostValue = data.reduce((sum, log) => sum + log.quantity * 1000, 0); // Assuming average ₦1000 per unit

    setStats({
      totalWriteOffs,
      recentWriteOffs,
      topReason,
      totalLostValue
    });
  };

  const getReasonIcon = (reason: string) => {
    switch (reason.toLowerCase()) {
      case 'expired':
        return <ExpiredIcon fontSize="small" />;
      case 'damaged':
        return <DangerousIcon fontSize="small" />;
      case 'stolen':
        return <WarningIcon fontSize="small" />;
      default:
        return <ErrorIcon fontSize="small" />;
    }
  };

  const getReasonColor = (reason: string) => {
    switch (reason.toLowerCase()) {
      case 'expired':
        return 'warning';
      case 'damaged':
        return 'error';
      case 'stolen':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredLogs = filter === 'all'
    ? logs
    : logs.filter(log => log.reason.toLowerCase().includes(filter.toLowerCase()));

  useEffect(() => {
    fetchLogs();
  }, []);

  return (
    <IonPage>
      <IonContent>
        <Box sx={{
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            px: { xs: 2, md: 4 },
            py: 4,
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <Container maxWidth="xl">
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={800} color="white" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      Stock Alerts & Write-Offs
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                      Monitor inventory losses, damages, and expired items
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={fetchLogs}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'rotate(180deg)',
                      transition: 'transform 0.5s ease'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Container>
          </Box>

          {/* Stats Cards */}
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(100, 116, 139, 0.2)',
                  height: '100%'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                      <WriteOffIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h3" fontWeight={700}>
                        {stats.totalWriteOffs}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Write-Offs
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(234, 88, 12, 0.2)',
                  height: '100%'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                      <TrendingDownIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h3" fontWeight={700}>
                        {stats.recentWriteOffs}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Last 30 Days
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(5, 150, 105, 0.2)',
                  height: '100%'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                      <WarningIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {stats.topReason}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Most Common Reason
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                  color: 'white',
                  boxShadow: '0 8px 25px rgba(124, 58, 237, 0.2)',
                  height: '100%'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                      <InventoryIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h3" fontWeight={700}>
                        ₦{(stats.totalLostValue / 1000).toFixed(0)}K
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Estimated Loss
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Container>

          {/* Filter Chips */}
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, pb: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#475569' }}>
                Filter by Reason:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {['all', 'Damaged', 'Expired', 'Stolen', 'Lost', 'Other'].map((reason) => (
                  <Chip
                    key={reason}
                    label={reason === 'all' ? 'All Write-Offs' : reason}
                    onClick={() => setFilter(reason)}
                    color={filter === reason ? 'error' : 'default'}
                    variant={filter === reason ? 'filled' : 'outlined'}
                    icon={reason !== 'all' ? getReasonIcon(reason) : undefined}
                    sx={{
                      mb: 1,
                      '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Container>

          {/* Main Table */}
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, pb: 4, flex: 1 }}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ p: 3, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  Write-Off History
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Detailed record of inventory items removed from stock
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flex: 1 }}>
                  <CircularProgress size={60} thickness={4} />
                </Box>
              ) : (
                <TableContainer sx={{ flex: 1, maxHeight: 'calc(100vh - 500px)' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>ITEM DETAILS</TableCell>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>LOCATION</TableCell>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>QUANTITY</TableCell>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>REASON</TableCell>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>DATE & TIME</TableCell>
                        <TableCell sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          fontSize: '0.875rem',
                          color: '#475569'
                        }}>AUTHORIZED BY</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                            <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
                              <InventoryIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                              <Typography variant="h6" fontWeight={600} color="#64748b">
                                No write-off records found
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {filter === 'all'
                                  ? 'No inventory items have been written off yet.'
                                  : `No write-offs found for "${filter}" reason.`
                                }
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : filteredLogs.map((log, index) => (
                        <TableRow
                          key={log.id}
                          hover
                          sx={{
                            '&:hover': { bgcolor: '#f8fafc' },
                            '&:last-child td': { borderBottom: 0 }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="#1e293b">
                                {log.stock_name}
                              </Typography>
                              <Typography variant="caption" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <InfoIcon fontSize="inherit" /> {log.ecid_item}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.from_location}
                              size="small"
                              icon={<LocationIcon fontSize="small" />}
                              variant="outlined"
                              sx={{ borderColor: '#cbd5e1' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: '#fee2e2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography variant="body2" fontWeight={700} color="#dc2626">
                                  {log.quantity}
                                </Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={600} color="#dc2626">
                                units lost
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.reason || 'General Loss'}
                              size="small"
                              color={getReasonColor(log.reason) as any}
                              icon={getReasonIcon(log.reason)}
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {new Date(log.date_time).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DateIcon fontSize="inherit" />
                                {new Date(log.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon fontSize="small" sx={{ color: '#64748b' }} />
                              {log.signer_out_name || 'System'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Container>

          {/* Warning Alert */}
          {stats.recentWriteOffs > 10 && (
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
              <MuiAlert
                severity="warning"
                variant="filled"
                icon={<WarningIcon />}
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 8px 20px rgba(245, 158, 11, 0.2)',
                  '& .MuiAlert-message': { flex: 1 }
                }}
              >
                <AlertTitle>High Write-Off Activity Detected</AlertTitle>
                <Typography variant="body2">
                  {stats.recentWriteOffs} items have been written off in the last 30 days.
                  Consider reviewing your inventory management practices.
                </Typography>
              </MuiAlert>
            </Container>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default StocksAlert;